// Enhanced Mega Menu Functionality
class MegaMenuEnhanced {
  constructor(megaMenuElement) {
    this.megaMenu = megaMenuElement;
    this.hoverTimeout = null;
    this.isHoverMode = false;
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.initializeFirstPanel();
    this.handleResize();
  }

  setupEventListeners() {
    // Add event listeners for L1 menu items (summary elements)
    const summary = this.megaMenu.querySelector('summary');
    if (summary) {
      // Remove existing listeners to avoid duplicates
      summary.removeEventListener('mouseenter', this.handleSummaryMouseEnter);
      summary.removeEventListener('mouseleave', this.handleSummaryMouseLeave);
      summary.removeEventListener('click', this.handleSummaryClick);

      // Bind methods to preserve context
      this.handleSummaryMouseEnter = () => {
        if (this.isHoverMode) {
          this.clearHoverTimeout();
          this.closeAllOtherMenus(); // Close other menus first
          this.openMenu();
          this.showHoverZone();
        }
      };

      this.handleSummaryMouseLeave = () => {
        if (this.isHoverMode) {
          // Don't close immediately - let the hover zone handle it
          // The hover zone will be shown via CSS :hover selector
        }
      };

      this.handleSummaryClick = (e) => {
        if (!this.isHoverMode) {
          // Let the default click behavior handle the menu toggle
          return;
        } else {
          // In hover mode, prevent default click behavior
          e.preventDefault();
        }
      };

      // Add event listeners
      summary.addEventListener('mouseenter', this.handleSummaryMouseEnter);
      summary.addEventListener('mouseleave', this.handleSummaryMouseLeave);
      summary.addEventListener('click', this.handleSummaryClick);
    }

    // Add event listeners for hover zones
    const hoverZone = this.megaMenu.querySelector('.mega-menu-hover-zone');
    if (hoverZone) {
      // Remove existing listeners to avoid duplicates
      hoverZone.removeEventListener('mouseenter', this.handleHoverZoneEnter);
      hoverZone.removeEventListener('mouseleave', this.handleHoverZoneLeave);

      // Bind methods to preserve context
      this.handleHoverZoneEnter = () => {
        if (this.isHoverMode) {
          this.clearHoverTimeout();
          this.closeAllOtherMenus(); // Close other menus first
          this.openMenu();
        }
      };

      this.handleHoverZoneLeave = () => {
        if (this.isHoverMode) {
          this.scheduleMenuClose();
          this.hideHoverZone();
        }
      };

      // Add event listeners
      hoverZone.addEventListener('mouseenter', this.handleHoverZoneEnter);
      hoverZone.addEventListener('mouseleave', this.handleHoverZoneLeave);
    }

    // Add hover event listeners to L2 navigation links
    const navLinks = this.megaMenu.querySelectorAll('.mega-menu-nav__link[data-l2-index]');
    
    navLinks.forEach(link => {
      link.addEventListener('mouseenter', (e) => {
        this.clearHoverTimeout();
        // Check if the link has L3 items by looking for the arrow SVG
        const hasArrow = e.target.querySelector('svg');
        console.log('Mouse enter on L2 link:', e.target.textContent.trim(), 'Has arrow:', !!hasArrow);
        if (hasArrow) {
          e.preventDefault();
          const l2Index = e.target.getAttribute('data-l2-index');
          console.log('Showing panel for L2 index:', l2Index);
          this.showPanel(l2Index);
          this.setActiveNavLink(e.target);
        } else {
          console.log('No arrow, hiding all panels');
          this.hideAllPanels();
        }
        // If no arrow, let the link behave normally
      });

      // Add click event listeners for mobile/touch devices
      link.addEventListener('click', (e) => {
        // Check if the link has L3 items by looking for the arrow SVG
        const hasArrow = e.target.querySelector('svg');
        if (hasArrow) {
          e.preventDefault();
          const l2Index = e.target.getAttribute('data-l2-index');
          this.showPanel(l2Index);
          this.setActiveNavLink(e.target);
        }
        // If no arrow, let the link navigate normally
      });
    });

    // Handle mouse enter/leave on the entire mega menu content
    const megaMenuContent = this.megaMenu.querySelector('.mega-menu__content');
    if (megaMenuContent) {
      // Remove existing listeners to avoid duplicates
      megaMenuContent.removeEventListener('mouseenter', this.handleContentMouseEnter);
      megaMenuContent.removeEventListener('mouseleave', this.handleContentMouseLeave);

      // Bind methods to preserve context
      this.handleContentMouseEnter = () => {
        if (this.isHoverMode) {
          this.clearHoverTimeout();
        }
      };

      this.handleContentMouseLeave = () => {
        if (this.isHoverMode) {
          this.scheduleMenuClose();
        }
      };

      // Add event listeners
      megaMenuContent.addEventListener('mouseenter', this.handleContentMouseEnter);
      megaMenuContent.addEventListener('mouseleave', this.handleContentMouseLeave);
    }

    // Add window resize listener
    window.removeEventListener('resize', this.handleResize);
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  initializeFirstPanel() {
    // Show the first panel by default
    this.showPanel('0');
    
    // Set the first nav link as active
    const firstNavLink = this.megaMenu.querySelector('.mega-menu-nav__link[data-l2-index="0"]');
    if (firstNavLink) {
      this.setActiveNavLink(firstNavLink);
    }
  }

  showPanel(l2Index) {
    // Hide all L3 panels
    this.hideAllPanels();

    // Show the selected L3 panel
    const targetPanel = this.megaMenu.querySelector(`.mega-menu-l3-panel[data-l2-index="${l2Index}"]`);
    if (targetPanel) {
      targetPanel.classList.add('mega-menu-l3-panel--active');
    }
  }

  hideAllPanels() {
    // Hide all L3 panels
    const panels = this.megaMenu.querySelectorAll('.mega-menu-l3-panel');
    panels.forEach(panel => {
      panel.classList.remove('mega-menu-l3-panel--active');
    });
  }

  setActiveNavLink(activeLink) {
    // Remove active class from all nav links
    const navLinks = this.megaMenu.querySelectorAll('.mega-menu-nav__link');
    navLinks.forEach(link => {
      link.classList.remove('mega-menu-nav__link--active');
    });

    // Add active class to the selected link
    if (activeLink) {
      activeLink.classList.add('mega-menu-nav__link--active');
    }
  }

  // New methods for hover functionality
  openMenu() {
    this.megaMenu.setAttribute('open', '');
  }

  closeMenu() {
    this.megaMenu.removeAttribute('open');
    this.hideHoverZone();
  }

  showHoverZone() {
    const hoverZone = this.megaMenu.querySelector('.mega-menu-hover-zone');
    if (hoverZone) {
      hoverZone.style.pointerEvents = 'auto';
      hoverZone.style.opacity = '1';
    }
  }

  hideHoverZone() {
    const hoverZone = this.megaMenu.querySelector('.mega-menu-hover-zone');
    if (hoverZone) {
      hoverZone.style.pointerEvents = 'none';
      hoverZone.style.opacity = '0';
    }
  }

  closeAllOtherMenus() {
    // Close all other mega menus except the current one
    const allMegaMenus = document.querySelectorAll('.mega-menu');
    allMegaMenus.forEach(menu => {
      if (menu !== this.megaMenu) {
        menu.removeAttribute('open');
        // Hide hover zones for other menus
        const otherHoverZone = menu.querySelector('.mega-menu-hover-zone');
        if (otherHoverZone) {
          otherHoverZone.style.pointerEvents = 'none';
          otherHoverZone.style.opacity = '0';
        }
      }
    });
  }

  clearHoverTimeout() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  scheduleMenuClose() {
    this.clearHoverTimeout();
    this.hoverTimeout = setTimeout(() => {
      this.closeMenu();
      this.hideHoverZone();
      // Reset to first panel when menu closes
      this.showPanel('0');
      const firstNavLink = this.megaMenu.querySelector('.mega-menu-nav__link[data-l2-index="0"]');
      if (firstNavLink) {
        this.setActiveNavLink(firstNavLink);
      }
    }, 100); // Shorter delay since we have hover zones
  }

  handleResize() {
    const wasHoverMode = this.isHoverMode;
    this.isHoverMode = window.innerWidth > 1360;
    
    // If mode changed, clear any pending timeouts and close menu
    if (wasHoverMode !== this.isHoverMode) {
      this.clearHoverTimeout();
      if (this.isHoverMode) {
        // Switching to hover mode - close any open menus
        this.closeMenu();
      }
    }
  }
}

// Global function to close all mega menus
function closeAllMegaMenus() {
  const allMegaMenus = document.querySelectorAll('.mega-menu');
  allMegaMenus.forEach(menu => {
    menu.removeAttribute('open');
    const hoverZone = menu.querySelector('.mega-menu-hover-zone');
    if (hoverZone) {
      hoverZone.style.pointerEvents = 'none';
      hoverZone.style.opacity = '0';
    }
  });
}

// Initialize the enhanced mega menu
function initializeMegaMenus() {
  console.log('MegaMenuEnhanced: Initializing...');
  const megaMenus = document.querySelectorAll('.mega-menu');
  console.log('MegaMenuEnhanced: Found', megaMenus.length, 'mega menus');
  
  megaMenus.forEach((megaMenu, index) => {
    console.log('MegaMenuEnhanced: Processing mega menu', index);
    const hasNewLayout = megaMenu.querySelector('.mega-menu-layout');
    console.log('MegaMenuEnhanced: Has new layout:', !!hasNewLayout);
    
    if (hasNewLayout && !megaMenu.classList.contains('mega-menu--enhanced')) {
      console.log('MegaMenuEnhanced: Enhancing mega menu', index);
      megaMenu.classList.add('mega-menu--enhanced');
      new MegaMenuEnhanced(megaMenu);
    }
  });
}

// Add global event listener for menu switching
document.addEventListener('mouseover', (e) => {
  // Check if hovering over a different mega menu summary
  const summary = e.target.closest('.mega-menu summary');
  if (summary && summary.closest('.mega-menu--enhanced')) {
    const currentMenu = summary.closest('.mega-menu');
    // Close all other menus except the current one
    const allMegaMenus = document.querySelectorAll('.mega-menu--enhanced');
    allMegaMenus.forEach(menu => {
      if (menu !== currentMenu) {
        menu.removeAttribute('open');
        const hoverZone = menu.querySelector('.mega-menu-hover-zone');
        if (hoverZone) {
          hoverZone.style.pointerEvents = 'none';
          hoverZone.style.opacity = '0';
        }
      }
    });
  }
});

// Try multiple initialization methods to ensure it runs
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMegaMenus);
} else {
  initializeMegaMenus();
}

// Also try after a short delay in case the menu is dynamically loaded
setTimeout(initializeMegaMenus, 100);
setTimeout(initializeMegaMenus, 500);
setTimeout(initializeMegaMenus, 1000);

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MegaMenuEnhanced;
}
